import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import PrintHeader from '../Common/PrintHeader';
import './Forms.css';

const PaidVoucherForm = () => {
  const { i18n } = useTranslation();
  const printRef = useRef(null);
  const isArabic = i18n.language === 'ar';
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    paidTo: '',
    amount: '',
    paymentType: 'cash', // 'cash' or 'bank'
    purpose: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handlePrint = async () => {
    try {
      const element = printRef.current;
      if (!element) {
        throw new Error('Print container not found');
      }
      
      // Store original styles
      const originalStyles = {
        visibility: element.style.visibility,
        position: element.style.position,
        left: element.style.left,
        top: element.style.top,
        width: element.style.width,
        height: element.style.height,
        zIndex: element.style.zIndex,
      };
      
      // Make element visible for capture
      element.style.visibility = 'visible';
      element.style.position = 'fixed';
      element.style.left = '0';
      element.style.top = '0';
      element.style.width = '210mm';
      element.style.height = '297mm';
      element.style.zIndex = '9999';
      element.style.backgroundColor = '#ffffff';
      
      // Wait for images to load
      const images = element.querySelectorAll('img');
      const imagePromises = Array.from(images).map((img) => {
        if (img.complete && img.naturalHeight !== 0) {
          return Promise.resolve();
        }
        return new Promise((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () => resolve(); // Resolve even on error to continue
          // Timeout after 5 seconds
          setTimeout(() => resolve(), 5000);
        });
      });
      
      await Promise.all(imagePromises);
      await new Promise(resolve => setTimeout(resolve, 500));

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        backgroundColor: '#ffffff',
        imageTimeout: 15000,
        removeContainer: false,
      });
      
      // Restore original styles
      Object.keys(originalStyles).forEach(key => {
        element.style[key] = originalStyles[key] || '';
      });
      
      if (!canvas || canvas.width === 0 || canvas.height === 0) {
        throw new Error('Failed to capture page content');
      }
      
      const imgData = canvas.toDataURL('image/png', 1.0);
      const pdf = new jsPDF('portrait', 'mm', 'a4');
      const pageWidth = 210;
      const imgWidth = pageWidth * 0.7; // 30% smaller
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const marginX = (pageWidth - imgWidth) / 2;
      
      pdf.addImage(imgData, 'PNG', marginX, 0, imgWidth, imgHeight);

      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, '_blank');
    } catch (error) {
      console.error('Print error:', error);
      alert('Failed to generate PDF: ' + error.message);
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-title">
          <i className="fas fa-file-invoice-dollar page-icon"></i>
          <h1>{isArabic ? 'سند صرف' : 'Paid Voucher'}</h1>
        </div>
      </div>

      <div className="form-container">
        <form>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-calendar"></i> {isArabic ? 'التاريخ' : 'Date'} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-calendar input-icon"></i>
                <input
                  type="date"
                  className="form-input"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-user"></i> {isArabic ? 'اسم المستلم' : 'Paid To'} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-user input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="paidTo"
                  value={formData.paidTo}
                  onChange={handleChange}
                  placeholder={isArabic ? 'أدخل اسم المستلم' : 'Enter paid to name'}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-money-bill-wave"></i> {isArabic ? 'المبلغ المدفوع' : 'Amount Paid'} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-money-bill-wave input-icon"></i>
                <input
                  type="number"
                  className="form-input"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  placeholder={isArabic ? 'أدخل المبلغ' : 'Enter amount'}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-money-check-alt"></i> {isArabic ? 'نوع المبلغ' : 'Payment Type'} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-money-check-alt input-icon"></i>
                <select
                  className="form-input"
                  name="paymentType"
                  value={formData.paymentType}
                  onChange={handleChange}
                  required
                >
                  <option value="cash">{isArabic ? 'نقداً' : 'Cash'}</option>
                  <option value="bank">{isArabic ? 'بنك' : 'Bank'}</option>
                </select>
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">
                <i className="fas fa-comment"></i> {isArabic ? 'الغرض' : 'Purpose'} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-comment input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="purpose"
                  value={formData.purpose}
                  onChange={handleChange}
                  placeholder={isArabic ? 'أدخل الغرض' : 'Enter purpose'}
                  required
                />
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handlePrint}
            >
              <i className="fas fa-print"></i> {isArabic ? 'طباعة PDF' : 'Print to PDF'}
            </button>
          </div>
        </form>
      </div>

      {/* Print Preview - Exact Match to Receipt Voucher Template */}
      <div 
        ref={printRef} 
        style={{ 
          position: 'absolute',
          left: '-9999px',
          top: 0,
          fontFamily: 'Arial, sans-serif',
          margin: 0,
          padding: '20px',
          backgroundColor: '#f5f5f5',
          width: '210mm',
          minHeight: '297mm',
          visibility: 'hidden',
          pointerEvents: 'none',
          overflow: 'hidden'
        }}
      >
        <div style={{
          maxWidth: '900px',
          margin: '0 auto',
          backgroundColor: 'white',
          padding: '30px',
          boxShadow: '0 0 10px rgba(0,0,0,0.1)',
          border: '3px solid #000000'
        }}>
          {/* Print Header with Organization Details */}
          <div style={{ marginBottom: '20px' }}>
            <PrintHeader 
              layout="voucher" 
              title={isArabic ? 'سند صرف' : 'Paid Voucher'}
              subtitle={isArabic ? 'Paid Voucher' : 'سند صرف'}
              showDate={false}
            />
          </div>
          
          {/* Second Row - Paid To */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '30px',
            gap: '10px'
          }}>
            {/* Right Side - Arabic */}
            <div style={{
              textAlign: 'right',
              color: '#bf4f1f',
              fontSize: '16px',
              fontWeight: 'bold',
              whiteSpace: 'nowrap'
            }}>
              اصرفوا الى المكرم /
            </div>

            {/* Middle - Thin line */}
            <div style={{
              flex: 1,
              borderBottom: '1px solid #000000',
              height: '1px'
            }}></div>
            
            {/* Left Side - English */}
            <div style={{
              textAlign: 'left',
              color: '#bf4f1f',
              fontSize: '16px',
              fontWeight: 'bold',
              whiteSpace: 'nowrap'
            }}>
              PAID TO
            </div>
          </div>
          
          {/* Third Row - The sum */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '20px',
            gap: '10px'
          }}>
            {/* Right Side - Arabic */}
            <div style={{
              textAlign: 'right',
              color: '#bf4f1f',
              fontSize: '16px',
              fontWeight: 'bold',
              whiteSpace: 'nowrap'
            }}>
              مبلغ وقدره
            </div>

            {/* Middle - Thin line */}
            <div style={{
              flex: 1,
              borderBottom: '1px solid #000000',
              height: '1px'
            }}></div>
              
            {/* Left Side - English */}
            <div style={{
              textAlign: 'left',
              color: '#bf4f1f',
              fontSize: '16px',
              fontWeight: 'bold',
              whiteSpace: 'nowrap'
            }}>
              The sum
            </div>
          </div>
          
          {/* Fourth Row - Payment Method */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            marginTop: '20px',
            gap: '5px',
            color: '#bf4f1f',
            fontSize: '14px',
            fontWeight: 'bold'
          }}>
            {/* Left checkboxes */}
            <input type="checkbox" style={{ width: '18px', height: '18px', accentColor: '#bf4f1f' }} />
            <span>نقداً</span>
            <input type="checkbox" style={{ width: '18px', height: '18px', accentColor: '#bf4f1f', marginLeft: '10px' }} />
            <span>شيك رقم</span>
            
            {/* Line for cheque number */}
            <span style={{ borderBottom: '1px solid #000000', flex: 1, margin: '0 10px' }}></span>
            
            <span>على بنك</span>
            
            {/* Line for bank name */}
            <span style={{ borderBottom: '1px solid #000000', flex: 1, margin: '0 10px' }}></span>
            
            <span>Bank</span>
            
            {/* Line */}
            <span style={{ borderBottom: '1px solid #000000', flex: 1, margin: '0 10px' }}></span>
            
            <span>Cheque No</span>
            <input type="checkbox" style={{ width: '18px', height: '18px', accentColor: '#bf4f1f', margin: '0 5px' }} />
            <span>Cash</span>
            <input type="checkbox" style={{ width: '18px', height: '18px', accentColor: '#bf4f1f', marginLeft: '5px' }} />
          </div>
          
          {/* Fifth Row - For/وذلك عن قيمة */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            marginTop: '20px',
            gap: '10px',
            color: '#bf4f1f',
            fontSize: '14px',
            fontWeight: 'bold'
          }}>
            <span>وذلك عن قيمة</span>
            <span style={{ borderBottom: '1px solid #000000', flex: 1 }}></span>
            <span>For</span>
          </div>
          
          {/* Two additional lines */}
          <div style={{ borderBottom: '1px solid #000000', marginTop: '15px' }}></div>
          <div style={{ borderBottom: '1px solid #000000', marginTop: '25px' }}></div>
          
          {/* Last Row - Signatures */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginTop: '30px',
            gap: '20px'
          }}>
            {/* Receiver المستلم */}
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{
                color: '#bf4f1f',
                fontSize: '14px',
                fontWeight: 'bold',
                marginBottom: '10px'
              }}>
                المستلم <span style={{ margin: '0 10px' }}>Receiver</span>
              </div>
              <div style={{
                borderBottom: '1px solid #000000',
                marginTop: '30px',
                display: 'inline-block',
                width: 'auto',
                minWidth: '150px'
              }}></div>
            </div>

            {/* Accountant المحاسب */}
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{
                color: '#bf4f1f',
                fontSize: '14px',
                fontWeight: 'bold',
                marginBottom: '10px'
              }}>
                المحاسب <span style={{ margin: '0 10px' }}>Accountant</span>
              </div>
              <div style={{
                borderBottom: '1px solid #000000',
                marginTop: '30px',
                display: 'inline-block',
                width: 'auto',
                minWidth: '150px'
              }}></div>
            </div>

            {/* Manager المدير */}
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{
                color: '#bf4f1f',
                fontSize: '14px',
                fontWeight: 'bold',
                marginBottom: '10px'
              }}>
                المدير <span style={{ margin: '0 10px' }}>Manager</span>
              </div>
              <div style={{
                borderBottom: '1px solid #000000',
                marginTop: '30px',
                display: 'inline-block',
                width: 'auto',
                minWidth: '150px'
              }}></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaidVoucherForm;
